import { useState, useEffect, useCallback, useId, useMemo, useRef } from 'react';
import { makeStyles, mergeClasses, shorthands, tokens, Input, Button, Text } from '@fluentui/react-components';
import {
	Dismiss20Regular,
	ChevronUp20Regular,
	ChevronDown20Regular,
	Search20Regular,
	Flash20Regular,
	Play20Regular,
	BotSparkle20Regular,
	BranchFork20Regular,
} from '@fluentui/react-icons';
import { useReactFlow, type Node } from '@xyflow/react';
import { ottoTokens } from '@/theme/ottoTokens';
import { useGetConnectorInfo } from '@/contexts';
import { getBuiltInManifest } from '@/data/manifests';
import { telemetryService } from '@/services/telemetryService';

/**
 * Minimum number of categorisable nodes a workflow must have before the
 * filter chips become eligible to appear. Below this threshold the flat
 * list is short enough to scan unaided.
 */
const FILTER_THRESHOLD = 25;

/**
 * Node types that we don't surface in search results. These are structural /
 * placeholder nodes that have no user-meaningful label to look up.
 */
const NON_SEARCHABLE_TYPES = new Set<string>([
	'scopeContainer',
	'merge',
	'phantom',
	'placeholderTrigger',
]);

type Category = 'all' | 'triggers' | 'actions' | 'agents' | 'controlFlow';

const FILTER_CATEGORIES: { id: Category; label: string }[] = [
	{ id: 'all', label: 'All' },
	{ id: 'triggers', label: 'Triggers' },
	{ id: 'actions', label: 'Actions' },
	{ id: 'agents', label: 'Agents' },
	{ id: 'controlFlow', label: 'Conditions & loops' },
];

function categorize(nodeType: string | undefined): Exclude<Category, 'all'> | null {
	switch (nodeType) {
		case 'trigger':
			return 'triggers';
		case 'action':
		case 'tool':
		case 'model':
			return 'actions';
		case 'agent':
			return 'agents';
		case 'if':
		case 'switch':
		case 'foreach':
			return 'controlFlow';
		default:
			return null;
	}
}

const useStyles = makeStyles({
	container: {
		position: 'absolute',
		top: tokens.spacingVerticalXL,
		left: '50%',
		transform: 'translateX(-50%)',
		zIndex: 1000,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		backgroundColor: tokens.colorNeutralBackground1,
		...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
		borderRadius: tokens.borderRadiusLarge,
		boxShadow: tokens.shadow16,
		backdropFilter: 'blur(10px)',
		minWidth: '560px',
		maxWidth: '680px',
		overflow: 'hidden',
		animationName: {
			from: { opacity: 0, transform: 'translate(-50%, -4px)' },
			to: { opacity: 1, transform: 'translate(-50%, 0)' },
		},
		animationDuration: tokens.durationNormal,
		animationTimingFunction: tokens.curveDecelerateMid,
		animationFillMode: 'both',
		transitionProperty: 'border-color, box-shadow',
		transitionDuration: tokens.durationFast,
		transitionTimingFunction: tokens.curveEasyEase,
		'&:focus-within': {
			...shorthands.borderColor(tokens.colorBrandStroke1),
			boxShadow: `${tokens.shadow16}, 0 0 0 1px ${tokens.colorBrandStroke1}`,
		},
		'@media (forced-colors: active)': {
			...shorthands.borderColor('CanvasText'),
		},
		'@media (prefers-reduced-motion: reduce)': {
			animationDuration: '0.01ms',
			transitionDuration: '0.01ms',
		},
	},
	bar: {
		display: 'flex',
		alignItems: 'center',
		gap: tokens.spacingHorizontalS,
		paddingLeft: tokens.spacingHorizontalM,
		paddingRight: tokens.spacingHorizontalXS,
		paddingTop: tokens.spacingVerticalXS,
		paddingBottom: tokens.spacingVerticalXS,
	},
	searchIcon: {
		flexShrink: 0,
		color: tokens.colorNeutralForeground3,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	input: {
		flex: 1,
		minWidth: '230px',
		// Strip Fluent Input's own chrome — the outer container provides border,
		// background, and focus ring so the whole row reads as one field.
		backgroundColor: 'transparent',
		...shorthands.border('none'),
		'&::after': {
			display: 'none',
		},
		'&:focus-within': {
			...shorthands.border('none'),
			outlineStyle: 'none',
		},
		// Hide the user-agent clear button that ships with type="search". Our
		// dismiss × on the right is the single source of truth for closing /
		// resetting; an inline clear would clash with it visually.
		'& input::-webkit-search-cancel-button': {
			WebkitAppearance: 'none',
			display: 'none',
		},
		'& input::-webkit-search-decoration': {
			WebkitAppearance: 'none',
			display: 'none',
		},
	},
	barDivider: {
		width: '1px',
		alignSelf: 'stretch',
		marginTop: tokens.spacingVerticalXS,
		marginBottom: tokens.spacingVerticalXS,
		backgroundColor: tokens.colorNeutralStroke2,
	},
	matchCount: {
		color: tokens.colorNeutralForeground2,
		fontSize: tokens.fontSizeBase200,
		whiteSpace: 'nowrap',
		minWidth: '50px',
		textAlign: 'center',
		fontVariantNumeric: 'tabular-nums',
	},
	navButton: {
		minWidth: 'auto',
	},
	results: {
		display: 'flex',
		flexDirection: 'column',
		borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
	},
	chipRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: tokens.spacingHorizontalXS,
		padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
		borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
	},
	chip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: tokens.spacingHorizontalXS,
		paddingTop: tokens.spacingVerticalXXS,
		paddingBottom: tokens.spacingVerticalXXS,
		paddingLeft: tokens.spacingHorizontalSNudge,
		paddingRight: tokens.spacingHorizontalSNudge,
		borderRadius: tokens.borderRadiusCircular,
		border: `1px solid ${tokens.colorNeutralStroke1}`,
		backgroundColor: 'transparent',
		color: tokens.colorNeutralForeground2,
		fontSize: tokens.fontSizeBase200,
		fontFamily: tokens.fontFamilyBase,
		cursor: 'pointer',
		transitionProperty: 'background-color, border-color, color, transform',
		transitionDuration: tokens.durationFast,
		transitionTimingFunction: tokens.curveEasyEase,
		'&:hover:not(:disabled)': {
			backgroundColor: tokens.colorNeutralBackground1Hover,
			color: tokens.colorNeutralForeground1,
		},
		'&:active:not(:disabled)': {
			transform: 'scale(0.96)',
		},
		'&:focus-visible': {
			outline: `2px solid ${tokens.colorStrokeFocus2}`,
			outlineOffset: '2px',
		},
		'&:disabled': {
			cursor: 'default',
			opacity: 0.6,
		},
		'@media (forced-colors: active)': {
			...shorthands.borderColor('CanvasText'),
		},
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0.01ms',
			'&:active:not(:disabled)': {
				transform: 'none',
			},
		},
	},
	chipSelected: {
		backgroundColor: tokens.colorBrandBackground2,
		...shorthands.borderColor(tokens.colorBrandStroke1),
		color: tokens.colorBrandForeground1,
		'&:hover:not(:disabled)': {
			backgroundColor: tokens.colorBrandBackground2Hover,
		},
	},
	chipCount: {
		opacity: 0.85,
		fontVariantNumeric: 'tabular-nums',
	},
	list: {
		listStyle: 'none',
		margin: 0,
		padding: tokens.spacingVerticalXXS,
		maxHeight: '320px',
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	option: {
		display: 'flex',
		alignItems: 'center',
		gap: tokens.spacingHorizontalS,
		padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
		borderRadius: tokens.borderRadiusSmall,
		cursor: 'pointer',
		color: tokens.colorNeutralForeground1,
		fontSize: tokens.fontSizeBase300,
		userSelect: 'none',
		'&:hover': {
			backgroundColor: tokens.colorNeutralBackground1Hover,
		},
	},
	optionActive: {
		backgroundColor: tokens.colorBrandBackground2,
		color: tokens.colorBrandForeground1,
		'&:hover': {
			backgroundColor: tokens.colorBrandBackground2Hover,
		},
	},
	optionIcon: {
		display: 'inline-flex',
		flexShrink: 0,
		width: '20px',
		height: '20px',
		alignItems: 'center',
		justifyContent: 'center',
	},
	optionIconImage: {
		width: '20px',
		height: '20px',
		borderRadius: tokens.borderRadiusSmall,
		objectFit: 'contain',
		display: 'block',
		// Subtle outline so connector tiles read as discrete glyphs against any
		// background. Pure white at low opacity for the dark theme.
		boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.1)',
	},
	optionLabel: {
		flex: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	optionMeta: {
		flexShrink: 0,
		fontSize: tokens.fontSizeBase200,
		color: tokens.colorNeutralForeground3,
		fontVariantNumeric: 'tabular-nums',
	},
	empty: {
		padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
		color: tokens.colorNeutralForeground3,
		fontSize: tokens.fontSizeBase200,
		textAlign: 'center',
	},
	srOnly: {
		position: 'absolute',
		width: '1px',
		height: '1px',
		padding: 0,
		margin: '-1px',
		overflow: 'hidden',
		clip: 'rect(0,0,0,0)',
		whiteSpace: 'nowrap',
		border: 0,
	},
	iconTrigger: { color: ottoTokens.nodeAccentTrigger },
	iconAction: { color: ottoTokens.nodeAccentAction },
	iconAgent: { color: ottoTokens.nodeAccentAgent },
	iconControlFlow: { color: ottoTokens.nodeAccentControlFlow },
	iconGeneric: { color: tokens.colorNeutralForeground3 },
});

export interface CanvasSearchProps {
	nodes: Node[];
	isOpen: boolean;
	onClose: () => void;
}

interface MatchEntry {
	node: Node;
	label: string;
	category: Exclude<Category, 'all'> | null;
	iconUri: string | undefined;
}

function readString(value: unknown): string {
	return typeof value === 'string' ? value : '';
}

function getNodeLabel(node: Node): string {
	return readString(node.data?.label) || readString(node.data?.actionName);
}

function resolveIconUri(
	node: Node,
	lookupConnector: (connectionName: string) => { iconUri?: string | undefined } | undefined,
): string | undefined {
	const connectionName = readString(node.data?.connectionName);
	if (connectionName) {
		const info = lookupConnector(connectionName);
		if (info?.iconUri) return info.iconUri;
	}
	const actionType = readString(node.data?.actionType);
	const kind = readString(node.data?.kind);
	if (actionType) {
		const manifest = getBuiltInManifest(actionType, kind || undefined);
		const manifestIcon = manifest?.properties?.iconUri;
		if (typeof manifestIcon === 'string' && manifestIcon.length > 0) return manifestIcon;
	}
	return undefined;
}

function getCategoryIcon(category: MatchEntry['category'], iconClass: IconStyles): JSX.Element {
	switch (category) {
		case 'triggers':
			return <Flash20Regular className={iconClass.iconTrigger} />;
		case 'actions':
			return <Play20Regular className={iconClass.iconAction} />;
		case 'agents':
			return <BotSparkle20Regular className={iconClass.iconAgent} />;
		case 'controlFlow':
			return <BranchFork20Regular className={iconClass.iconControlFlow} />;
		default:
			return <Play20Regular className={iconClass.iconGeneric} />;
	}
}

function getCategoryLabel(category: MatchEntry['category']): string {
	switch (category) {
		case 'triggers':
			return 'Trigger';
		case 'actions':
			return 'Action';
		case 'agents':
			return 'Agent';
		case 'controlFlow':
			return 'Condition or loop';
		default:
			return 'Node';
	}
}

type IconStyles = {
	optionIconImage: string;
	iconTrigger: string;
	iconAction: string;
	iconAgent: string;
	iconControlFlow: string;
	iconGeneric: string;
};

/**
 * Renders a connector icon for a match row, gracefully falling back to the
 * category glyph when the URI is missing or the image fails to load. Kept
 * as a sub-component so per-row error state stays local to each option.
 */
const MatchIcon: React.FC<{
	iconUri: string | undefined;
	category: MatchEntry['category'];
	styles: IconStyles;
}> = ({ iconUri, category, styles }) => {
	const [errored, setErrored] = useState(false);
	if (iconUri && !errored) {
		return (
			<img
				src={iconUri}
				alt=""
				aria-hidden="true"
				className={styles.optionIconImage}
				onError={() => setErrored(true)}
			/>
		);
	}
	return getCategoryIcon(category, styles);
};

export const CanvasSearch: React.FC<CanvasSearchProps> = ({ nodes, isOpen, onClose }) => {
	const styles = useStyles();
	const reactFlowInstance = useReactFlow();
	const lookupConnector = useGetConnectorInfo();
	const baseId = useId();
	const listboxId = `${baseId}-listbox`;
	const liveRegionId = `${baseId}-live`;
	const optionId = useCallback((nodeId: string) => `${baseId}-opt-${nodeId}`, [baseId]);

	const [searchQuery, setSearchQuery] = useState('');
	const [activeFilter, setActiveFilter] = useState<Category>('all');
	const [activeMatchId, setActiveMatchId] = useState<string | null>(null);

	const listRef = useRef<HTMLUListElement>(null);
	const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

	const normalizedQuery = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);
	const hasQuery = normalizedQuery.length > 0;

	// All matches (across categories) for the current query.
	const allMatches = useMemo<MatchEntry[]>(() => {
		if (!normalizedQuery) return [];
		const out: MatchEntry[] = [];
		for (const node of nodes) {
			if (node.type && NON_SEARCHABLE_TYPES.has(node.type)) continue;
			const label = readString(node.data?.label).toLowerCase();
			const actionName = readString(node.data?.actionName).toLowerCase();
			if (label.includes(normalizedQuery) || actionName.includes(normalizedQuery)) {
				out.push({
					node,
					label: getNodeLabel(node),
					category: categorize(node.type),
					iconUri: resolveIconUri(node, lookupConnector),
				});
			}
		}
		return out;
	}, [nodes, normalizedQuery, lookupConnector]);

	// Per-category match counts (for chip labels).
	const counts = useMemo(() => {
		const c = { all: allMatches.length, triggers: 0, actions: 0, agents: 0, controlFlow: 0 };
		for (const m of allMatches) {
			if (m.category) c[m.category] += 1;
		}
		return c;
	}, [allMatches]);

	// Filtered matches (what the user actually sees & navigates).
	const visibleMatches = useMemo(() => {
		if (activeFilter === 'all') return allMatches;
		return allMatches.filter((m) => m.category === activeFilter);
	}, [allMatches, activeFilter]);

	// Number of nodes that can fall into one of the filter-chip categories.
	// This is what actually drives whether chips would be useful — purely
	// searchable-but-uncategorised nodes (notes, future node types we don't
	// know about) wouldn't be reachable via any chip and shouldn't push us
	// toward showing them.
	const categorisableNodeCount = useMemo(() => {
		let n = 0;
		for (const node of nodes) {
			if (categorize(node.type) !== null) n += 1;
		}
		return n;
	}, [nodes]);

	// Number of distinct categories the current matches span. Filtering only
	// helps when results live in more than one bucket; if everything is in
	// "Actions" then selecting "Actions" wouldn't narrow anything.
	const categoriesWithMatches = useMemo(() => {
		let n = 0;
		if (counts.triggers > 0) n += 1;
		if (counts.actions > 0) n += 1;
		if (counts.agents > 0) n += 1;
		if (counts.controlFlow > 0) n += 1;
		return n;
	}, [counts]);

	// Surface filter chips only when (a) the workflow is non-trivial AND
	// (b) the current matches actually span enough categories for the chips
	// to do useful narrowing. Either gate alone produces useless filter UI:
	// the size gate alone shows chips for single-category result sets; the
	// category gate alone clutters tiny workflows that don't need filters.
	const showFilters = categorisableNodeCount > FILTER_THRESHOLD && categoriesWithMatches >= 2;

	// Index of the active match within the visible list (-1 if no longer visible).
	const activeIndex = useMemo(() => {
		if (!activeMatchId) return -1;
		return visibleMatches.findIndex((m) => m.node.id === activeMatchId);
	}, [visibleMatches, activeMatchId]);

	// Track query for debounced telemetry only.
	useEffect(() => {
		if (!hasQuery) return;
		clearTimeout(searchDebounceRef.current);
		searchDebounceRef.current = setTimeout(() => {
			telemetryService.trackEvent('canvas.search.query', {
				query_length: searchQuery.length,
				match_count: allMatches.length,
			});
		}, 500);
		return () => clearTimeout(searchDebounceRef.current);
	}, [hasQuery, searchQuery.length, allMatches.length]);

	// Pan/select on the canvas for the given node id.
	const focusNode = useCallback(
		(nodeId: string, direction: 'next' | 'previous' | 'auto' | 'click' = 'auto') => {
			reactFlowInstance.fitView({
				nodes: [{ id: nodeId }],
				duration: 300,
				padding: 0.3,
			});
			reactFlowInstance.setNodes((current) =>
				current.map((n) => ({ ...n, selected: n.id === nodeId })),
			);
			telemetryService.trackEvent('canvas.search.navigate', {
				direction,
				match_index: visibleMatches.findIndex((m) => m.node.id === nodeId) + 1,
				total_matches: visibleMatches.length,
				filter: activeFilter,
			});
		},
		[reactFlowInstance, visibleMatches, activeFilter],
	);

	// Keep active match valid as visibleMatches changes.
	useEffect(() => {
		if (visibleMatches.length === 0) {
			if (activeMatchId !== null) setActiveMatchId(null);
			return;
		}
		const stillVisible = activeMatchId && visibleMatches.some((m) => m.node.id === activeMatchId);
		if (!stillVisible) {
			const first = visibleMatches[0]!.node.id;
			setActiveMatchId(first);
			focusNode(first, 'auto');
		}
	}, [visibleMatches, activeMatchId, focusNode]);

	// Scroll the active option into view inside the listbox.
	useEffect(() => {
		if (activeIndex < 0 || !listRef.current) return;
		const el = listRef.current.querySelector<HTMLElement>(`[data-active="true"]`);
		if (el && typeof el.scrollIntoView === 'function') {
			el.scrollIntoView({ block: 'nearest' });
		}
	}, [activeIndex]);

	const goToNextMatch = useCallback(() => {
		if (visibleMatches.length === 0) return;
		const next = ((activeIndex < 0 ? -1 : activeIndex) + 1 + visibleMatches.length) % visibleMatches.length;
		const id = visibleMatches[next]!.node.id;
		setActiveMatchId(id);
		focusNode(id, 'next');
	}, [visibleMatches, activeIndex, focusNode]);

	const goToPreviousMatch = useCallback(() => {
		if (visibleMatches.length === 0) return;
		const prev = (activeIndex <= 0 ? visibleMatches.length : activeIndex) - 1;
		const id = visibleMatches[prev]!.node.id;
		setActiveMatchId(id);
		focusNode(id, 'previous');
	}, [visibleMatches, activeIndex, focusNode]);

	const handleSelectFilter = useCallback(
		(filter: Category) => {
			if (filter === activeFilter) return;
			setActiveFilter(filter);
			telemetryService.trackEvent('canvas.search.filter', {
				filter,
				query_length: searchQuery.length,
				category_match_count:
					filter === 'all'
						? counts.all
						: counts[filter as Exclude<Category, 'all'>],
				total_matches: counts.all,
			});
		},
		[activeFilter, counts, searchQuery.length],
	);

	// Global keyboard handlers.
	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				onClose();
				return;
			}

			// Navigation keys only act when we have results to step through.
			if (visibleMatches.length === 0) return;

			if (event.key === 'Enter') {
				event.preventDefault();
				if (event.shiftKey) {
					goToPreviousMatch();
				} else {
					goToNextMatch();
				}
				return;
			}

			if (event.key === 'ArrowDown') {
				event.preventDefault();
				goToNextMatch();
				return;
			}

			if (event.key === 'ArrowUp') {
				event.preventDefault();
				goToPreviousMatch();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onClose, goToNextMatch, goToPreviousMatch, visibleMatches.length]);

	// Reset state on actual open→close transitions.
	const wasOpenRef = useRef(isOpen);
	useEffect(() => {
		const wasOpen = wasOpenRef.current;
		wasOpenRef.current = isOpen;
		if (wasOpen && !isOpen) {
			setSearchQuery('');
			setActiveFilter('all');
			setActiveMatchId(null);
			reactFlowInstance.setNodes((current) => current.map((n) => ({ ...n, selected: false })));
		}
	}, [isOpen, reactFlowInstance]);

	if (!isOpen) return null;

	const totalForCounter = visibleMatches.length;
	const counterCurrent = activeIndex >= 0 ? activeIndex + 1 : 0;
	const showResults = hasQuery; // dropdown frame visible whenever the user has typed
	// The listbox is only present in the DOM when there are matches to render.
	// Empty states (no matches in All, or no matches in the sticky category)
	// render a plain text element instead. Reflect that in the ARIA wiring so
	// `aria-controls`/`aria-expanded` never reference a non-existent element.
	const listboxRendered = showResults && counts.all > 0 && visibleMatches.length > 0;
	const activeOptionId = activeIndex >= 0 ? optionId(visibleMatches[activeIndex]!.node.id) : undefined;

	const filterLabel = FILTER_CATEGORIES.find((c) => c.id === activeFilter)?.label ?? 'All';
	const liveMessage = !hasQuery
		? ''
		: counts.all === 0
			? 'No matches'
			: totalForCounter === 0
				? `No matches in ${filterLabel}. ${counts.all} total matches.`
				: activeFilter === 'all'
					? `${counterCurrent} of ${totalForCounter} matches.`
					: `${counterCurrent} of ${totalForCounter} matches in ${filterLabel}.`;

	return (
		<div className={styles.container} role="search">
			<div className={styles.bar}>
				<span className={styles.searchIcon} aria-hidden="true">
					<Search20Regular />
				</span>
				<Input
					className={styles.input}
					type="search"
					placeholder="Search nodes..."
					aria-label="Search nodes"
					aria-controls={listboxRendered ? listboxId : undefined}
					aria-expanded={listboxRendered}
					aria-activedescendant={activeOptionId}
					aria-describedby={liveRegionId}
					value={searchQuery}
					onChange={(_, data) => setSearchQuery(data.value)}
					appearance="outline"
					autoFocus
				/>
				<Text className={styles.matchCount} aria-hidden="true">
					{hasQuery ? `${counterCurrent} of ${totalForCounter}` : ''}
				</Text>
				<span className={styles.barDivider} aria-hidden="true" />
				<Button
					className={styles.navButton}
					appearance="subtle"
					icon={<ChevronUp20Regular />}
					onClick={goToPreviousMatch}
					disabled={totalForCounter === 0}
					title="Previous match (Shift+Enter)"
					aria-label="Previous match"
				/>
				<Button
					className={styles.navButton}
					appearance="subtle"
					icon={<ChevronDown20Regular />}
					onClick={goToNextMatch}
					disabled={totalForCounter === 0}
					title="Next match (Enter)"
					aria-label="Next match"
				/>
				<Button
					className={styles.navButton}
					appearance="subtle"
					icon={<Dismiss20Regular />}
					onClick={onClose}
					title="Close (Escape)"
					aria-label="Close search"
				/>
			</div>

			<span id={liveRegionId} className={styles.srOnly} role="status" aria-live="polite">
				{liveMessage}
			</span>

			{showResults && (
				<div className={styles.results}>
					{showFilters && counts.all > 0 && (
						<div
							className={styles.chipRow}
							role="group"
							aria-label="Filter results by category"
						>
							{FILTER_CATEGORIES.map((cat) => {
								const count = cat.id === 'all' ? counts.all : counts[cat.id];
								const isSelected = activeFilter === cat.id;
								const disabled = cat.id !== 'all' && count === 0 && !isSelected;
								return (
									<button
										key={cat.id}
										type="button"
										className={mergeClasses(
											styles.chip,
											isSelected && styles.chipSelected,
										)}
										onClick={() => handleSelectFilter(cat.id)}
										aria-pressed={isSelected}
										disabled={disabled}
									>
										<span>{cat.label}</span>
										<span className={styles.chipCount} aria-label={`${count} ${count === 1 ? 'match' : 'matches'}`}>
											{count}
										</span>
									</button>
								);
							})}
						</div>
					)}

					{counts.all === 0 ? (
						<div className={styles.empty}>No matches</div>
					) : visibleMatches.length === 0 ? (
						<div className={styles.empty}>
							No matches in this category — switch to <strong>All</strong> to see {counts.all}.
						</div>
					) : (
						<ul
							id={listboxId}
							ref={listRef}
							className={styles.list}
							role="listbox"
							aria-label="Matching nodes"
						>
							{visibleMatches.map((match) => {
								const isActive = match.node.id === activeMatchId;
								return (
									<li
										key={match.node.id}
										id={optionId(match.node.id)}
										role="option"
										aria-selected={isActive}
										data-active={isActive || undefined}
										className={mergeClasses(styles.option, isActive && styles.optionActive)}
										onMouseDown={(e) => {
											// Avoid stealing focus from the input.
											e.preventDefault();
										}}
										onClick={() => {
											setActiveMatchId(match.node.id);
											focusNode(match.node.id, 'click');
										}}
									>
										<span className={styles.optionIcon} aria-hidden="true">
											<MatchIcon iconUri={match.iconUri} category={match.category} styles={styles} />
										</span>
										<span className={styles.optionLabel} title={match.label}>
											{match.label || '(unnamed)'}
										</span>
										<span className={styles.optionMeta}>{getCategoryLabel(match.category)}</span>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			)}
		</div>
	);
};
